import cn from "classnames"
import React, { useCallback } from "react"
import { useTranslation, Trans } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import ClientData from "@/common/client_data"
import Scrollable from "@/common/components/scrollable"
import { isTablet } from "@/common/util"
import { ReactComponent as MulticolorIcon } from "@/images/signomatic/multicolor.svg"
import { ReactComponent as OutdoorIcon } from "@/images/signomatic/outdoor.svg"
import Icons from "@/images/signomatic/signtypes"
import SelectedIndicator from "@/signomatic/components/selected_indicator"
import Toolbox from "@/signomatic/components/toolbox"
import { type ToolboxStripItemProps } from "@/signomatic/components/toolbox_strip"
import { actions } from "@/signomatic/store/actions"
import { type State } from "@/signomatic/store/reducers"

const description = (type: string): string => {
  if (isTablet()) {
    type += "_short"
  }

  return `product_toolbox.${type}`
}

interface PropertyProps {
  text: string
  image: any
}

const Property = ({ text, image }: PropertyProps) => (
  <div className="property">
    {React.createElement(image)}
    <p className="paragraph-xs bold center">{text}</p>
  </div>
)

interface ProductDetailsProps {
  text: string
  image: string
  outdoor: boolean
  multicolor: boolean
}

const ProductDetails = ({ text, image, outdoor, multicolor }: ProductDetailsProps) => {
  const { t } = useTranslation()

  return (
    <div className="described-details product">
      <img src={image} alt="" />
      <div className="properties">
        {multicolor && <Property image={MulticolorIcon} text={t("properties.multicolor")} />}
        {outdoor && <Property image={OutdoorIcon} text={t("properties.outdoor")} />}
      </div>
      <div className="description" dangerouslySetInnerHTML={{ __html: text }} />
    </div>
  )
}

interface ProductItemProps {
  name: string
  active: boolean
  outdoor: boolean
  multicolor: boolean
  adminOnly: boolean
  onReadMore: (e: React.MouseEvent) => void
  onClick: (type: string) => void
}

const ProductItem: React.FC<ProductItemProps> = ({
  name,
  active,
  outdoor,
  multicolor,
  adminOnly,
  onReadMore,
  onClick,
}) => {
  const { t } = useTranslation()
  const properties = outdoor || multicolor

  return (
    <div className={cn("described", name, { active })} onClick={() => onClick(name)}>
      <div className="contents">
        <div className="image">{Icons[name] ? React.createElement(Icons[name]) : ""}</div>
        <div className="details">
          <h5 className="mb-0">{t(`sign_type.${name}`) + (adminOnly ? " - SignAdmin users only" : "")}</h5>
          <p className="paragraph-2xs mb-0" dangerouslySetInnerHTML={{ __html: t(description(name)) }} />
          <div className="info-row">
            <p className="paragraph-xs mb-0">
              <a onClick={onReadMore}>
                {t("misc.readmore")}
                ...
              </a>
            </p>
            {!properties && <SelectedIndicator active={active} />}
          </div>
          {properties && (
            <div className="info-row props">
              <span className="properties">
                {outdoor && <Property image={OutdoorIcon} text={t("properties.outdoor")} />}
                {multicolor && <Property image={MulticolorIcon} text={t("properties.multicolor")} />}
              </span>
              <SelectedIndicator active={active} />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

interface ProductToolboxStateProps {
  signs: SignType[]
  signType: string
  subToolbox: string | null
  metaProduct: string
}

const originalData = ClientData.signData

const ProductToolbox: React.FC<ToolboxStripItemProps> = ({ sign }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { signs, signType, subToolbox, metaProduct } = useSelector<State, ProductToolboxStateProps>(
    ({ signs, signType, subToolbox, metaProduct }) => ({
      signs,
      signType,
      subToolbox,
      metaProduct,
    })
  )
  const dispatch = useDispatch()

  const admin = ClientData.admin && ClientData.admin.id

  const productSigns = signs.filter(sign => {
    if (admin) {
      return !sign.skip_product
    } else {
      return !sign.skip_product && sign.admin_only === false
    }
  })
  const currentSign = subToolbox ? signs.find(sign => sign.key === subToolbox) : null

  const changeSign = useCallback(
    (type: string) => {
      const signData = signs.find(item => item.key === type)
      const currentData = originalData?.find(data => data.name === type)

      if (
        sign.state &&
        sign.state.doubleSided &&
        !currentData?.double_sided &&
        sign.state.objects.some(o => o.side !== 0)
      ) {
        if (!confirm(t("signomatic.objects_side_move"))) {
          return
        }

        sign.state.objects.forEach(element => {
          if (element.side !== 0) {
            sign.deleteObject(element)
          }
        })
      }

      if (currentData) {
        dispatch(actions.setStateChangeToolbox(type))
      }

      const path = signData?.signomatic_path || ""

      navigate(path)
    },
    [signs, sign, navigate, t, dispatch]
  )

  return (
    <Toolbox tKey="products">
      <Scrollable scrollToActive scrollActiveToolboxDelay>
        {productSigns.map(sign => (
          <ProductItem
            key={sign.key}
            name={sign.key}
            outdoor={sign.outdoor}
            multicolor={sign.multicolor}
            adminOnly={sign.admin_only}
            active={metaProduct ? metaProduct == sign.key : signType === sign.key}
            onClick={changeSign}
            onReadMore={e => {
              e.stopPropagation()
              e.preventDefault()

              dispatch(actions.setSubToolbox(sign.key))
            }}
          />
        ))}
        <div className="assortment-link">
          <Trans
            i18nKey={"toolbox.assortment_link"}
            components={{ a: <a href={ClientData.signsPath}>assortment</a> }}
          />
        </div>
      </Scrollable>
      {subToolbox && currentSign && (
        <Scrollable>
          <ProductDetails
            text={currentSign.text}
            image={currentSign.image}
            outdoor={currentSign.outdoor}
            multicolor={currentSign.multicolor}
          />
        </Scrollable>
      )}
    </Toolbox>
  )
}

export default ProductToolbox
